import { Injectable, inject } from '@angular/core';
import { joinRefusalKey } from '@app/app/shared/helpers/join-state.helper';
import { NestedPathHelper } from '@app/app/shared/helpers/nested-path.helper';
import { JoinStateDetails, PlaylistCardShort } from '@app/app/shared/models';
import { TOAST_NOTIFICATION_SERVICE } from '@app/app/shared/services/toast-notifications/toast-service';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { JOIN_DATA_SERVICE, JoinAction, JoinTarget } from './join-data.service';

/** Joins content for the learner and reports a refusal, the same way wherever the content is shown. */
@Injectable({ providedIn: 'root' })
export class ContentJoinService {
  private readonly dataService = inject(JOIN_DATA_SERVICE);
  private readonly toast = inject(TOAST_NOTIFICATION_SERVICE);
  private readonly transloco = inject(TranslocoService);

  private readonly refusedSubject = new Subject<void>();
  /** A refusal means the state the learner saw is out of date: pages reload their content on it. */
  readonly refused$ = this.refusedSubject.asObservable();

  /** @return the new join state, or null once a refusal was shown */
  act(target: JoinTarget, action: JoinAction): Observable<JoinStateDetails | null> {
    return this.dataService.act(target, action).pipe(
      map((result) => {
        if (result.isSuccess) {
          return result.value;
        }
        this.toast.showFail(this.transloco.translate(joinRefusalKey(result.error)));
        this.refusedSubject.next();
        return null;
      }),
    );
  }
}

/** Listings mark playlists by type, and some by their playlist type alone. */
export function isPlaylistItem(item: PlaylistCardShort): boolean {
  return item.type === 'PLAYLIST' || !!item.collectionType;
}

/**
 * An item listed on a page: playlists are joined in the context of the page they're listed on, cards on their own.
 *
 * @param pageUrl - the URL of the page listing the item
 */
export function joinTargetOfItem(item: PlaylistCardShort, pageUrl: string): JoinTarget {
  if (!isPlaylistItem(item)) {
    return { kind: 'card', id: item._id };
  }
  const path = NestedPathHelper.parsePath(pageUrl.split('?')[0])?.fullPath;
  return { kind: 'playlist', uid: item._id, ...(path ? { in: path } : {}) };
}
